/**
 * Car detail (`/garage/:id`).
 *
 * Three views in one page:
 *   - Top action row: back button on the left; for owners, an icon-only
 *     row of Share / Edit / More on the right.
 *   - Header: image-or-icon + name + manufacturer/model. For the owner,
 *     hovering (desktop) or tapping (mobile) the image opens a small
 *     popover with upload/replace/remove/change-icon actions — see
 *     `CarMediaPicker`.
 *   - Stats: owner, avg L/100km, current odometer, fuel category + preferred fuel.
 *   - Owner-only sections: share list, transfer, delete (the share/edit/transfer/
 *     delete triggers all live in the top row, but their modals + the share list
 *     live further down).
 *   - Shared-user section: leave car button.
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import CarIcon from "../../components/CarIcon";
import Icon from "../../components/Icon";
import { ApiError } from "../../api/client";
import {
  useCar,
  useDeleteCar,
  useDeleteCarImage,
  useInviteShare,
  useRemoveShare,
  useRevokeShareInvite,
  useUpdateShareRole,
  useUpdateCar,
  useUploadCarImage,
} from "../../api/cars";
import { useAuth } from "../../auth/AuthProvider";
import type {
  CarDetail,
  CarIconName,
  PendingShareInvite,
  ShareView,
} from "../../api/types";
import {
  CAR_ICONS,
  FUEL_CATEGORIES,
  fuelTypeLabel,
} from "./carFormConstants";
import EditCarModal from "./EditCarModal";
import TransferCarModal from "./TransferCarModal";
import Modal from "../../components/Modal";
import MoreMenu, {
  MoreMenuDivider,
  MoreMenuItem,
} from "../account/MoreMenu";

function formatNumber(value: number | null, digits = 1): string {
  if (value === null) return "—";
  return value.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function categoryLabel(value: string): string {
  return FUEL_CATEGORIES.find((c) => c.value === value)?.label ?? value;
}

export default function CarDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const id = Number(params.id);
  const carQuery = useCar(Number.isFinite(id) ? id : undefined);

  if (carQuery.isLoading) {
    return (
      <section className="page-section">
        <p className="empty-state">Loading…</p>
      </section>
    );
  }

  if (carQuery.isError || !carQuery.data) {
    const message =
      carQuery.error instanceof ApiError
        ? carQuery.error.status === 404
          ? "This car doesn't exist or you don't have access to it."
          : carQuery.error.message
        : "Couldn't load this car.";
    return (
      <section className="page-section">
        <div className="empty-state">
          <p>{message}</p>
          <button
            type="button"
            className="button"
            onClick={() => navigate("/garage")}
          >
            Back to Garage
          </button>
        </div>
      </section>
    );
  }

  return <CarDetailView car={carQuery.data.car} />;
}

function CarDetailView({ car }: { car: CarDetail }) {
  const navigate = useNavigate();
  const { me } = useAuth();
  const isOwner = car.access_level === "owner";

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);

  const isActive = me?.last_active_car_id === car.id;

  return (
    <>
      <section className="page-section">
        <div className="car-detail-nav">
          <button
            type="button"
            className="button button--ghost button--small"
            onClick={() => navigate("/garage")}
          >
            ← Back to Garage
          </button>
          {isOwner ? (
            <div className="car-detail-actions">
              <button
                type="button"
                className="button button--ghost car-detail-action"
                aria-label="Share car"
                title="Share car"
                onClick={() => setIsShareOpen(true)}
              >
                <Icon name="user-plus" />
              </button>
              <button
                type="button"
                className="button button--ghost car-detail-action"
                aria-label="Edit car"
                title="Edit car"
                onClick={() => setIsEditOpen(true)}
              >
                <Icon name="edit" />
              </button>
              <CarOwnerMoreMenu
                car={car}
                onTransfer={() => setIsTransferOpen(true)}
              />
            </div>
          ) : null}
        </div>
        <CarHeader car={car} isOwner={isOwner} isActive={isActive} />
      </section>

      <section className="page-section">
        <div className="stat-grid">
          <Stat label="Owner" value={isOwner ? "You" : car.owner_username} />
          <Stat
            label="All-time avg"
            value={
              car.avg_liters_per_100km !== null
                ? `${formatNumber(car.avg_liters_per_100km)} L/100km`
                : "—"
            }
          />
          <Stat
            label="Current odometer"
            value={
              car.current_odometer_km !== null
                ? `${formatNumber(car.current_odometer_km, 0)} km`
                : "—"
            }
          />
          <Stat label="Fuel category" value={categoryLabel(car.fuel_category)} />
          <Stat
            label="Preferred fuel"
            value={fuelTypeLabel(car.preferred_fuel_type)}
          />
        </div>

        {car.notes && (
          <p className="car-notes">
            <strong>Notes: </strong>
            {car.notes}
          </p>
        )}
      </section>

      {isOwner && <ShareSection car={car} />}
      {!isOwner && <LeaveSection car={car} />}

      {isOwner && (
        <>
          <EditCarModal
            isOpen={isEditOpen}
            onClose={() => setIsEditOpen(false)}
            car={car}
          />
          <TransferCarModal
            isOpen={isTransferOpen}
            onClose={() => setIsTransferOpen(false)}
            carId={car.id}
            carName={car.name}
          />
          <ShareCarModal
            car={car}
            isOpen={isShareOpen}
            onClose={() => setIsShareOpen(false)}
          />
        </>
      )}
    </>
  );
}

function CarHeader({
  car,
  isOwner,
  isActive,
}: {
  car: CarDetail;
  isOwner: boolean;
  isActive: boolean;
}) {
  const subtitle = [car.manufacturer, car.model].filter(Boolean).join(" · ");
  return (
    <div className="car-header">
      {isOwner ? (
        <CarMediaPicker car={car} />
      ) : (
        <div className="car-header__media">
          {car.image_url ? (
            <img
              src={car.image_url}
              alt=""
              className="car-header__image"
            />
          ) : (
            <CarIcon name={car.icon} size={64} className="car-header__icon" />
          )}
        </div>
      )}
      <div className="car-header__body">
        <div className="car-header__title-row">
          <h1 className="car-header__name">{car.name}</h1>
          {isActive ? <span className="badge badge--active">Active</span> : null}
        </div>
        {subtitle && <p className="car-header__sub">{subtitle}</p>}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat-tile">
      <p className="stat-tile__label">{label}</p>
      <p className="stat-tile__value">{value}</p>
    </div>
  );
}

/**
 * Owner-only header media that doubles as the upload / icon-change control.
 *
 * Desktop: hovering the image reveals a camera overlay; clicking opens a
 * popover anchored beneath the image. Mobile (no hover): any tap on the
 * image opens the popover directly — no overlay shown.
 *
 * Popover state machine:
 *   - "actions" view: 2 buttons that depend on whether an image is set.
 *     - image set     → [Replace image] [Remove image]
 *     - no image      → [Upload image]  [Change icon]
 *   - "icons" view (only reachable via "Change icon"): 5-icon grid with
 *     the current selection highlighted; selecting one PATCHes the car
 *     and returns to the "actions" view.
 *
 * Hidden file <input> wired up for the upload/replace flow.
 */
function CarMediaPicker({ car }: { car: CarDetail }) {
  const upload = useUploadCarImage(car.id);
  const remove = useDeleteCarImage(car.id);
  const update = useUpdateCar(car.id);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"actions" | "icons">("actions");
  const [error, setError] = useState<string | null>(null);

  // Close on outside-click / Escape. Reset to actions view on close.
  useEffect(() => {
    if (!open) return;
    function handleClick(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setView("actions");
      setError(null);
    }
  }, [open]);

  function handlePick() {
    fileRef.current?.click();
  }

  async function handleFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setError(null);
    try {
      await upload.mutateAsync(file);
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed.");
    }
  }

  async function handleRemove() {
    setError(null);
    try {
      await remove.mutateAsync();
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't remove image.");
    }
  }

  async function handlePickIcon(name: CarIconName) {
    setError(null);
    try {
      await update.mutateAsync({ icon: name });
      setOpen(false);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Couldn't change icon.",
      );
    }
  }

  const busy = upload.isPending || remove.isPending || update.isPending;
  const hasImage = Boolean(car.image_url);

  return (
    <div ref={containerRef} className="car-header__picker">
      <button
        type="button"
        className="car-header__media car-header__media--interactive"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={hasImage ? "Change car photo or icon" : "Add car photo or change icon"}
        onClick={() => setOpen((v) => !v)}
      >
        {hasImage ? (
          <img
            src={car.image_url!}
            alt=""
            className="car-header__image"
          />
        ) : (
          <CarIcon name={car.icon} size={64} className="car-header__icon" />
        )}
        <span className="car-header__overlay" aria-hidden="true">
          <CameraIcon />
        </span>
      </button>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFile}
      />

      {open && (
        <div
          className="more-menu__panel car-header__menu"
          role="menu"
        >
          {view === "actions" ? (
            hasImage ? (
              <>
                <button
                  type="button"
                  role="menuitem"
                  className="more-menu__item"
                  onClick={handlePick}
                  disabled={busy}
                >
                  Replace image
                </button>
                <div className="more-menu__divider" role="separator" />
                <button
                  type="button"
                  role="menuitem"
                  className="more-menu__item more-menu__item--danger"
                  onClick={handleRemove}
                  disabled={busy}
                >
                  Remove image
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  role="menuitem"
                  className="more-menu__item"
                  onClick={handlePick}
                  disabled={busy}
                >
                  Upload image
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="more-menu__item"
                  onClick={() => setView("icons")}
                  disabled={busy}
                >
                  Change icon
                </button>
              </>
            )
          ) : (
            <>
              <button
                type="button"
                className="more-menu__item car-header__menu-back"
                onClick={() => setView("actions")}
              >
                ← Back
              </button>
              <div className="more-menu__divider" role="separator" />
              <div className="icon-picker car-header__menu-icons">
                {CAR_ICONS.map((opt) => (
                  <button
                    key={opt.name}
                    type="button"
                    aria-pressed={car.icon === opt.name}
                    aria-label={opt.label}
                    className={
                      "icon-picker__btn" +
                      (car.icon === opt.name
                        ? " icon-picker__btn--active"
                        : "")
                    }
                    onClick={() => handlePickIcon(opt.name)}
                    disabled={busy}
                  >
                    <CarIcon name={opt.name} size={32} />
                  </button>
                ))}
              </div>
            </>
          )}
          {error && (
            <p
              className="form-status car-header__menu-error"
              data-variant="error"
              role="alert"
            >
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function CameraIcon() {
  // Inline Lucide-style camera glyph (16x16). Stays in lockstep with the
  // rest of the icon family without pulling in another IconSprite entry.
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14.5 4h-5l-2 3H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3.5l-2-3z" />
      <circle cx="12" cy="13" r="3.5" />
    </svg>
  );
}

function ShareSection({ car }: { car: CarDetail }) {
  return (
    <section className="page-section">
      <h2 className="section-title">Sharing</h2>
      {car.shares.length > 0 ? (
        <ShareUserList car={car} />
      ) : (
        <p className="section-help">This car is not shared with anyone.</p>
      )}
    </section>
  );
}

function ShareCarModal({
  car,
  isOpen,
  onClose,
}: {
  car: CarDetail;
  isOpen: boolean;
  onClose: () => void;
}) {
  const invite = useInviteShare(car.id);
  const revoke = useRevokeShareInvite(car.id);
  const [login, setLogin] = useState("");
  const [role, setRole] = useState<"editor" | "viewer">("editor");
  const [inviteConfirm, setInviteConfirm] = useState<{
    login: string;
    role: "editor" | "viewer";
  } | null>(null);
  const [revokeConfirm, setRevokeConfirm] =
    useState<PendingShareInvite | null>(null);
  const [feedback, setFeedback] = useState<{
    text: string;
    variant?: "error" | "success";
  } | null>(null);

  function handleInvite(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = login.trim();
    if (!trimmed) return;
    setFeedback(null);
    setInviteConfirm({ login: trimmed, role });
  }

  async function confirmInvite() {
    if (!inviteConfirm) return;
    setFeedback(null);
    try {
      const result = await invite.mutateAsync(inviteConfirm);
      setLogin("");
      setInviteConfirm(null);
      setFeedback({
        text:
          result.status === "updated"
            ? "Updated existing share."
            : "Invite sent.",
        variant: "success",
      });
    } catch (err) {
      setFeedback({
        text: err instanceof ApiError ? err.message : "Couldn't send invite.",
        variant: "error",
      });
    }
  }

  async function confirmRevokeInvite() {
    if (!revokeConfirm) return;
    setFeedback(null);
    try {
      await revoke.mutateAsync(revokeConfirm.notification_id);
      setRevokeConfirm(null);
      setFeedback({ text: "Invite revoked.", variant: "success" });
    } catch (err) {
      setFeedback({
        text: err instanceof ApiError ? err.message : "Couldn't revoke invite.",
        variant: "error",
      });
    }
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="Share car">
        <form className="auth-form" onSubmit={handleInvite} noValidate>
          <div className="field">
            <label className="field__label" htmlFor="invite-login">
              Invite by username or email
            </label>
            <input
              id="invite-login"
              className="field__input"
              type="text"
              placeholder="username or email"
              autoCapitalize="none"
              autoCorrect="off"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="invite-role">
              Role
            </label>
            <select
              id="invite-role"
              className="field__input field__select"
              value={role}
              onChange={(e) => setRole(e.target.value as "editor" | "viewer")}
            >
              <option value="editor">Editor</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
          <div className="form-actions">
            <button
              type="submit"
              className="button button--primary"
              disabled={invite.isPending || !login.trim()}
            >
              Invite
            </button>
          </div>
          {feedback && (
            <p
              className="form-status"
              data-variant={feedback.variant === "error" ? "error" : undefined}
              role="alert"
            >
              {feedback.text}
            </p>
          )}
        </form>

        {car.pending_share_invites.length > 0 ? (
          <div className="share-modal__pending">
            <h3 className="share-modal__subtitle">Pending invites</h3>
            <ul className="share-list">
              {car.pending_share_invites.map((pending) => (
                <PendingInviteRow
                  key={pending.notification_id}
                  invite={pending}
                  onRevoke={() => setRevokeConfirm(pending)}
                  disabled={revoke.isPending}
                />
              ))}
            </ul>
          </div>
        ) : (
          <p className="section-help share-modal__pending-empty">
            No pending invites.
          </p>
        )}
      </Modal>

      <ConfirmActionModal
        isOpen={inviteConfirm !== null}
        title="Send invite?"
        body={
          inviteConfirm
            ? `Invite ${inviteConfirm.login} as ${inviteConfirm.role}?`
            : ""
        }
        confirmLabel="Send invite"
        isPending={invite.isPending}
        onCancel={() => setInviteConfirm(null)}
        onConfirm={() => void confirmInvite()}
      />
      <ConfirmActionModal
        isOpen={revokeConfirm !== null}
        title="Revoke invite?"
        body={
          revokeConfirm
            ? `${revokeConfirm.username} will no longer be able to accept this invitation.`
            : ""
        }
        confirmLabel="Revoke invite"
        destructive
        isPending={revoke.isPending}
        onCancel={() => setRevokeConfirm(null)}
        onConfirm={() => void confirmRevokeInvite()}
      />
    </>
  );
}

function ShareUserList({ car }: { car: CarDetail }) {
  const updateRole = useUpdateShareRole(car.id);
  const remove = useRemoveShare(car.id);
  const [roleConfirm, setRoleConfirm] = useState<{
    share: ShareView;
    role: "editor" | "viewer";
  } | null>(null);
  const [removeConfirm, setRemoveConfirm] = useState<ShareView | null>(null);
  const [feedback, setFeedback] = useState<{
    text: string;
    variant?: "error" | "success";
  } | null>(null);

  async function confirmRoleChange() {
    if (!roleConfirm) return;
    setFeedback(null);
    try {
      await updateRole.mutateAsync({
        userId: roleConfirm.share.user_id,
        role: roleConfirm.role,
      });
      setRoleConfirm(null);
      setFeedback({ text: "Permissions updated.", variant: "success" });
    } catch (err) {
      setFeedback({
        text:
          err instanceof ApiError
            ? err.message
            : "Couldn't update permissions.",
        variant: "error",
      });
    }
  }

  async function confirmRemove() {
    if (!removeConfirm) return;
    setFeedback(null);
    try {
      await remove.mutateAsync(removeConfirm.user_id);
      setRemoveConfirm(null);
      setFeedback({ text: "User removed.", variant: "success" });
    } catch (err) {
      setFeedback({
        text: err instanceof ApiError ? err.message : "Couldn't remove user.",
        variant: "error",
      });
    }
  }

  return (
    <>
      <ul className="share-list">
        {car.shares.map((s) => (
          <ShareRow
            key={s.user_id}
            share={s}
            onRoleChange={(role) => setRoleConfirm({ share: s, role })}
            onRemove={() => setRemoveConfirm(s)}
            disabled={updateRole.isPending || remove.isPending}
          />
        ))}
      </ul>
      {feedback && (
        <p
          className="form-status share-list__status"
          data-variant={feedback.variant === "error" ? "error" : undefined}
          role="alert"
        >
          {feedback.text}
        </p>
      )}
      <ConfirmActionModal
        isOpen={roleConfirm !== null}
        title="Change permissions?"
        body={
          roleConfirm
            ? `Change ${roleConfirm.share.username} to ${roleConfirm.role}?`
            : ""
        }
        confirmLabel="Change"
        isPending={updateRole.isPending}
        onCancel={() => setRoleConfirm(null)}
        onConfirm={() => void confirmRoleChange()}
      />
      <ConfirmActionModal
        isOpen={removeConfirm !== null}
        title="Remove user?"
        body={
          removeConfirm
            ? `${removeConfirm.username} will lose access to this car.`
            : ""
        }
        confirmLabel="Remove"
        destructive
        isPending={remove.isPending}
        onCancel={() => setRemoveConfirm(null)}
        onConfirm={() => void confirmRemove()}
      />
    </>
  );
}

function ShareRow({
  share,
  onRoleChange,
  onRemove,
  disabled,
}: {
  share: ShareView;
  onRoleChange: (role: "editor" | "viewer") => void;
  onRemove: () => void;
  disabled: boolean;
}) {
  return (
    <li className="share-row">
      <span className="share-row__user">
        <Icon name="user" />
        <span className="share-row__name">{share.username}</span>
      </span>
      <label className="sr-only" htmlFor={`share-role-${share.user_id}`}>
        Permissions for {share.username}
      </label>
      <select
        id={`share-role-${share.user_id}`}
        className="field__input field__select share-row__role"
        value={share.role}
        onChange={(event) =>
          onRoleChange(event.target.value as "editor" | "viewer")
        }
        disabled={disabled}
      >
        <option value="editor">Editor</option>
        <option value="viewer">Viewer</option>
      </select>
      <button
        type="button"
        className="button button--ghost button--icon share-row__remove"
        aria-label={`Remove ${share.username}`}
        title={`Remove ${share.username}`}
        onClick={onRemove}
        disabled={disabled}
      >
        <Icon name="user-x" />
      </button>
    </li>
  );
}

function PendingInviteRow({
  invite,
  onRevoke,
  disabled,
}: {
  invite: PendingShareInvite;
  onRevoke: () => void;
  disabled: boolean;
}) {
  return (
    <li className="share-row">
      <span className="share-row__user">
        <Icon name="user" />
        <span className="share-row__name">{invite.username}</span>
      </span>
      <span className="badge badge--neutral">Pending</span>
      <span className="share-row__role-text">{invite.role}</span>
      <button
        type="button"
        className="button button--ghost button--icon share-row__remove"
        aria-label={`Revoke invite for ${invite.username}`}
        title={`Revoke invite for ${invite.username}`}
        onClick={onRevoke}
        disabled={disabled}
      >
        <Icon name="user-x" />
      </button>
    </li>
  );
}

function CarOwnerMoreMenu({
  car,
  onTransfer,
}: {
  car: CarDetail;
  onTransfer: () => void;
}) {
  const navigate = useNavigate();
  const remove = useDeleteCar(car.id);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleDelete() {
    setError(null);
    if (car.shares.length > 0) {
      setError("Remove all shares before deleting this car.");
      return;
    }
    try {
      await remove.mutateAsync();
      navigate("/garage", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError("Remove all shares before deleting this car.");
      } else {
        setError(
          err instanceof ApiError ? err.message : "Couldn't delete the car.",
        );
      }
    }
  }

  return (
    <>
      <MoreMenu
        triggerIcon="menu"
        triggerAriaLabel="More actions"
        triggerClassName="car-detail-action"
        render={() => (
          <>
            <MoreMenuItem label="Transfer ownership..." onSelect={onTransfer} />
            <MoreMenuDivider />
            <MoreMenuItem
              label="Delete car"
              destructive
              onSelect={() => {
                setError(null);
                setConfirmDeleteOpen(true);
              }}
            />
          </>
        )}
      />
      <ConfirmActionModal
        isOpen={confirmDeleteOpen}
        title="Delete car?"
        body={`Delete "${car.name}"? This removes all fuel and odometer entries for this car. This cannot be undone.`}
        confirmLabel="Delete car"
        destructive
        isPending={remove.isPending}
        error={error}
        onCancel={() => {
          setError(null);
          setConfirmDeleteOpen(false);
        }}
        onConfirm={() => void handleDelete()}
      />
    </>
  );
}

function ConfirmActionModal({
  isOpen,
  title,
  body,
  confirmLabel,
  destructive,
  isPending,
  error,
  onCancel,
  onConfirm,
}: {
  isOpen: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  destructive?: boolean;
  isPending: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal isOpen={isOpen} onClose={onCancel} title={title}>
      <p className="confirm-modal__body">{body}</p>
      {error ? (
        <p className="form-status" data-variant="error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="form-actions">
        <button
          type="button"
          className="button button--ghost"
          onClick={onCancel}
          disabled={isPending}
        >
          Cancel
        </button>
        <button
          type="button"
          className={
            "button " + (destructive ? "button--danger" : "button--primary")
          }
          onClick={onConfirm}
          disabled={isPending}
        >
          {isPending ? "Working..." : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

function LeaveSection({ car }: { car: CarDetail }) {
  const { me } = useAuth();
  const remove = useRemoveShare(car.id);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  async function handleLeave() {
    if (!me) return;
    if (
      !window.confirm(
        `Leave "${car.name}"? You'll lose access until the owner re-invites you.`,
      )
    ) {
      return;
    }
    setError(null);
    try {
      await remove.mutateAsync(me.id);
      navigate("/garage", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't leave the car.");
    }
  }

  return (
    <section className="page-section">
      <h2 className="section-title">Leave this car</h2>
      <p className="section-help">
        You'll lose access until {car.owner_username} re-invites you. Your
        existing entries on this car remain.
      </p>
      <div className="form-actions">
        <button
          type="button"
          className="button button--danger"
          onClick={handleLeave}
          disabled={remove.isPending}
        >
          {remove.isPending ? "Leaving…" : "Leave car"}
        </button>
      </div>
      {error && (
        <p className="form-status" data-variant="error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
